import React, { ReactNode } from "react"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { Text } from "@/components/Primitives/Text"
import { Icon } from "@/components/Primitives/Icon"
import { JourneyStage } from "@/types/quests"
import { HomeIcon } from "@heroicons/react/24/outline"
import { useNavigate, useLocation } from "react-router-dom"
import { StageDropdownMenu } from "../StageDropdownMenu"
import { Estimate } from "../Estimate"

interface QuestLayoutProps {
  children: ReactNode
  stageName?: string
  missionName?: string
  questName: string
  journeyProgress: number
  stages?: JourneyStage[]
  currentStageIndex?: number
  completedStages?: Set<string>
  onStageSelect?: (index: number) => void
  accordionValue?: string
  setAccordionValue?: (value: string) => void
  footerContent?: ReactNode
  calculateStageProgress?: (stageId: string) => number
  questId?: string
  isStageNavigable?: (index: number) => boolean
  isQuestComplete?: boolean
  prep_time?: string | null
  action_time?: string | null
}

export function QuestLayout({
  children,
  stageName,
  missionName,
  questName,
  journeyProgress,
  stages = [],
  currentStageIndex = 0,
  completedStages = new Set(),
  onStageSelect,
  footerContent,
  calculateStageProgress = () => 0,
  questId,
  isStageNavigable = (index) => {
    if (index === currentStageIndex) return true

    if (index < currentStageIndex) {
      return stages[index] && completedStages.has(stages[index].id)
    }

    if (index > currentStageIndex) {
      for (let i = 0; i <= currentStageIndex; i++) {
        if (stages[i] && !completedStages.has(stages[i].id)) {
          return false
        }
      }
      return true
    }

    return false
  },
  isQuestComplete,
  prep_time,
  action_time,
}: QuestLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const isMissionPage = location.pathname?.includes("/missions/")

  const isStageCompleted = (stageName?: string) => {
    if (!stageName || !stages.length) return false

    const stageIndex = stages.findIndex(
      (stage) => stage.stage.title === stageName,
    )
    if (stageIndex === -1) return false

    return completedStages.has(stages[stageIndex].id)
  }

  const handleStageClick = () => {
    if (!stageName) return

    navigate(`/quests/${questId}`)

    if (onStageSelect) {
      const stageIndex = stages.findIndex(
        (stage) => stage.stage.title === stageName,
      )
      if (stageIndex !== -1) {
        onStageSelect(stageIndex)
      }
    }
  }

  return (
    <div className="container mx-auto space-y-8">
      <div className="flex items-center justify-between w-full mb-0">
        <div className="flex items-center text-sm" data-testid="breadcrumb">
          <Link
            to="/quests"
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon icon={HomeIcon} size="sm" className="text-gray-600" />
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
          <Link
            to={`/quests/${questId}`}
            className="text-muted-foreground hover:text-foreground"
          >
            {questName}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />

          {isMissionPage && (
            <>
              <span
                className={`${
                  isStageCompleted(stageName)
                    ? "text-muted-foreground hover:text-foreground cursor-pointer"
                    : "text-muted-foreground cursor-default"
                }`}
                onClick={handleStageClick}
              >
                {stageName}
              </span>
              <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
            </>
          )}
        </div>

        <StageDropdownMenu
          data-testid="stage-dropdown"
          stages={stages}
          currentStageIndex={currentStageIndex}
          completedStages={completedStages}
          onStageSelect={onStageSelect}
          calculateStageProgress={calculateStageProgress}
          isStageNavigable={isStageNavigable}
          journeyProgress={journeyProgress}
        />
      </div>

      <div className="mt-0 w-3/4">
        <Text variant="h1" bold className="text-base">
          {isQuestComplete
            ? questName
            : isMissionPage
              ? missionName
              : stageName}
        </Text>
        {isMissionPage && (
          <Estimate
            prep_time={prep_time ?? null}
            action_time={action_time ?? null}
            className="mt-2"
          />
        )}
      </div>

      <div className="space-y-4 relative mb-4">{children}</div>

      {footerContent && (
        <div className="border-t pt-4 md:flex md:justify-end">
          {footerContent}
        </div>
      )}
    </div>
  )
}
